import { useMemo, useState } from "react";
import { Button, cx } from "./ui";
import {
  CheckIcon,
  XIcon,
  DownloadIcon,
  ListIcon,
  CheckCircleIcon,
  AlertIcon,
} from "./ui/Icons";

export const rowError = (f) =>
  f.message ||
  (typeof f.error === "string" ? f.error : "") ||
  f.errors?.[0]?.message ||
  "Unknown error";

/** Merges pushed + failed into one ordered, row-numbered trace. */
export function buildLog(result) {
  return [
    ...(result.pushed || []).map((p) => ({
      rowId: p.rowId,
      rowNumber: p.rowNumber,
      label: p.label || p.rowId,
      detail: p.detail,
      ok: true,
    })),
    ...(result.failed || []).map((f) => ({
      rowNumber: f.rowNumber,
      label: f.label || f.rowId,
      detail: f.detail,
      ok: false,
      message: rowError(f),
    })),
  ].sort((a, b) => (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
}

export const logToText = (log) =>
  log
    .map(
      (e) =>
        `${e.ok ? "OK  " : "FAIL"}  #${e.rowNumber ?? "?"}  ${e.label}${
          e.detail ? `  |  ${e.detail}` : ""
        }${e.ok ? "" : `\n        ↳ ${e.message}`}`
    )
    .join("\n");

export function Stat({ label, value, tone = "neutral" }) {
  const box = {
    neutral: "border-ink-200 bg-white text-ink-900",
    muted: "border-ink-200 bg-ink-50/70 text-ink-600",
    success: "border-emerald-200 bg-emerald-50/70 text-emerald-700",
    danger: "border-red-200 bg-red-50/70 text-red-700",
  };
  const lab = {
    neutral: "text-ink-500",
    muted: "text-ink-500",
    success: "text-emerald-700",
    danger: "text-red-700",
  };
  return (
    <div className={cx("rounded-xl border p-3.5", box[tone])}>
      <p className={cx("text-[10.5px] font-semibold uppercase tracking-wider", lab[tone])}>
        {label}
      </p>
      <p className="mt-1 text-[24px] font-semibold leading-none tabular-nums">
        {value}
      </p>
    </div>
  );
}

/**
 * Row-by-row push trace. Rendered full-width so long errors stay readable.
 *
 * onVoid, voidedIds and voidingId are only passed for function types where
 * un-pushing a row is actually possible (Receive Money/Purchases via void,
 * Contacts via a real delete) — omitted everywhere else, so the button
 * never appears for a type that has no way to act on it. actionLabel
 * distinguishes the wording ("Void"/"Voided" vs "Delete"/"Deleted") since
 * Contacts' delete is irreversible in a way voiding isn't.
 */
export default function PushLog({
  result,
  onCopy,
  onDownload,
  dense = false,
  onVoid,
  voidedIds,
  voidingId,
  actionLabel = "Void",
}) {
  const [filter, setFilter] = useState("all");
  const log = useMemo(() => buildLog(result), [result]);
  const shown = filter === "failed" ? log.filter((e) => !e.ok) : log;
  const failedCount = (result.failed || []).length;

  if (!log.length) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-ink-200">
      <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 bg-ink-50 px-4 py-2.5">
        <ListIcon size={15} className="text-ink-500" />
        <p className="text-[12.5px] font-semibold text-ink-800">Push log</p>

        <div className="ml-2 flex rounded-lg bg-ink-200/60 p-0.5">
          {[
            { id: "all", label: `All ${log.length}` },
            { id: "failed", label: `Failed ${failedCount}` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={cx(
                "rounded-md px-2.5 py-1 text-[12px] font-medium transition",
                filter === t.id
                  ? "bg-white text-ink-900 shadow-sm"
                  : "text-ink-500 hover:text-ink-800"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {(onCopy || onDownload) && (
          <div className="ml-auto flex gap-1.5">
            {onCopy && (
              <Button size="sm" variant="secondary" onClick={() => onCopy(shown)}>
                Copy
              </Button>
            )}
            {onDownload && (
              <Button
                size="sm"
                variant="secondary"
                icon={DownloadIcon}
                onClick={() => onDownload(shown, filter === "failed" ? "errors" : "log")}
              >
                Download
              </Button>
            )}
          </div>
        )}
      </div>

      <ul
        className={cx(
          "divide-y divide-ink-100 overflow-y-auto bg-white",
          dense ? "max-h-72" : "max-h-[calc(100vh-360px)] min-h-[220px]"
        )}
      >
        {shown.map((e, i) => (
          <li
            key={i}
            className={cx("flex gap-3 px-4 py-2.5", !e.ok && "bg-red-50/40")}
          >
            <span
              className={cx(
                "mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full",
                e.ok ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"
              )}
            >
              {e.ok ? (
                <CheckIcon size={12} strokeWidth={3} />
              ) : (
                <XIcon size={12} strokeWidth={3} />
              )}
            </span>

            <span className="mt-0.5 shrink-0 font-mono text-[11px] font-semibold text-ink-400">
              #{e.rowNumber ?? "?"}
            </span>

            <div className="min-w-0 flex-1">
              <p
                className={cx(
                  "break-words text-[12.5px]",
                  e.ok ? "text-ink-700" : "font-medium text-ink-900"
                )}
              >
                {e.label}
              </p>
              {e.detail && (
                <p className="mt-0.5 break-words font-mono text-[11.5px] text-ink-400">
                  {e.detail}
                </p>
              )}
              {!e.ok && (
                <p className="mt-1 break-words text-[12.5px] leading-relaxed text-red-700">
                  {e.message}
                </p>
              )}
            </div>

            {onVoid && e.ok && e.rowId && (
              voidedIds?.has(e.rowId) ? (
                <span className="mt-0.5 shrink-0 self-start rounded bg-ink-100 px-1.5 py-0.5 text-[11px] font-medium text-ink-500">
                  {actionLabel === "Delete" ? "Deleted" : "Voided"}
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="shrink-0 self-start"
                  loading={voidingId === e.rowId}
                  onClick={() => onVoid(e.rowId)}
                >
                  {actionLabel}
                </Button>
              )
            )}
          </li>
        ))}
      </ul>

      {shown.length === 0 && (
        <div className="flex items-center justify-center gap-2 bg-white px-4 py-8 text-[13px] text-emerald-700">
          <CheckCircleIcon size={16} />
          No failures — every row went through.
        </div>
      )}
    </div>
  );
}

/**
 * The one outcome that needs acting on rather than retrying: Naqood created
 * the record but we failed to mark it synced. Pushing again duplicates it.
 */
export function OrphanBanner({ orphaned }) {
  if (!orphaned?.length) return null;
  return (
    <div className="overflow-hidden rounded-xl border-2 border-amber-400 bg-amber-50">
      <div className="flex items-start gap-2.5 border-b border-amber-200 px-4 py-3">
        <AlertIcon size={17} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="min-w-0">
          <p className="text-[13.5px] font-bold text-amber-900">
            {orphaned.length} row{orphaned.length === 1 ? " was" : "s were"} created in
            Naqood but not recorded here
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-amber-800">
            These exist in your books already. They are still marked unsynced on
            this side, so pushing again <span className="font-semibold">would
            create duplicates</span>. Check them in Naqood before doing anything
            else.
          </p>
        </div>
      </div>
      <ul className="divide-y divide-amber-200 bg-white">
        {orphaned.map((o, i) => (
          <li key={i} className="px-4 py-2.5">
            <p className="text-[12.5px] font-medium text-ink-900">
              #{o.rowNumber} · {o.label}
            </p>
            {o.detail && (
              <p className="mt-0.5 font-mono text-[11.5px] text-ink-400">{o.detail}</p>
            )}
            <p className="mt-1 break-words text-[12.5px] leading-relaxed text-amber-800">
              {o.message}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Banner shown above the log when a run didn't complete normally. */
export function RunProblemBanner({ status, error }) {
  if (status !== "stuck" && status !== "failed" && !error) return null;
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
      <AlertIcon size={16} className="mt-0.5 shrink-0 text-amber-600" />
      <div className="min-w-0">
        <p className="text-[13px] font-semibold text-amber-900">
          {status === "stuck"
            ? "This push never finished"
            : "This push failed before it could run"}
        </p>
        {error && (
          <p className="mt-1 break-words text-[12.5px] leading-relaxed text-amber-800">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
